// src/pages/painel/componentes/ModalPixCheckout.jsx
import React, { useEffect, useRef, useState, useCallback } from "react";
import "./modalPixCheckout.css";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCopy,
  faCheck,
  faCircleCheck,
  faCircleExclamation,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";

const API_BASE = process.env.REACT_APP_API_BASE;
const INTERVALO_POLLING_MS = 4000;

const ModalPixCheckout = ({ pacote, onClose, onPagamentoConfirmado }) => {
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState("");

  const [correlationId, setCorrelationId] = useState(null);
  const [pixCode, setPixCode] = useState("");
  const [qrcodeImage, setQrcodeImage] = useState(null);
  const [moedas, setMoedas] = useState(0);

  // 🆕 bônus da primeira recarga
  const [bonusMoedas, setBonusMoedas] = useState(0);

  const [copiado, setCopiado] = useState(false);
  const [pagamentoConfirmado, setPagamentoConfirmado] = useState(false);

  const intervaloRef = useRef(null);
  const jaGerouRef = useRef(false);

  useEffect(() => {
    if (jaGerouRef.current) return;
    jaGerouRef.current = true;

    const gerarPix = async () => {
      setCarregando(true);
      setErro("");

      try {
        const res = await fetch(`${API_BASE}/gerar-checkout-pix.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ pacote_id: pacote.id }),
        });

        const data = await res.json();

        if (data.status === "sucesso") {
          setCorrelationId(data.correlation_id);
          setPixCode(data.pix_code);
          setQrcodeImage(data.qrcode_image);
          setMoedas(data.moedas);
        } else {
          setErro(data.mensagem || "Erro ao gerar o Pix.");
        }
      } catch {
        setErro("Erro ao comunicar com o servidor.");
      }

      setCarregando(false);
    };

    gerarPix();
  }, [pacote.id]);

  const verificarStatus = useCallback(async () => {
    if (!correlationId || pagamentoConfirmado) return;

    try {
      const res = await fetch(
        `${API_BASE}/verificar-status-pix.php?correlation_id=${correlationId}`,
        { credentials: "include" }
      );
      const data = await res.json();

      if (data.status === "sucesso" && data.pago) {
        setBonusMoedas(data.bonus_moedas ?? 0); // 🆕
        setPagamentoConfirmado(true);
        onPagamentoConfirmado?.(data.moedas + (data.bonus_moedas ?? 0));
      }
    } catch {
      // silencioso — só tenta de novo no próximo ciclo
    }
  }, [correlationId, pagamentoConfirmado, onPagamentoConfirmado]);

  useEffect(() => {
    if (!correlationId || pagamentoConfirmado) return;

    intervaloRef.current = setInterval(verificarStatus, INTERVALO_POLLING_MS);

    return () => {
      if (intervaloRef.current) clearInterval(intervaloRef.current);
    };
  }, [correlationId, pagamentoConfirmado, verificarStatus]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        verificarStatus();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [verificarStatus]);

  const copiarCodigo = async () => {
    try {
      await navigator.clipboard.writeText(pixCode);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2500);
    } catch {
      // fallback silencioso
    }
  };

  return (
    <div className="pix-modal-fundo" onClick={onClose}>
      <div className="pix-modal-caixa" onClick={(e) => e.stopPropagation()}>
        <button className="pix-modal-fechar" onClick={onClose} aria-label="Fechar">
          <FontAwesomeIcon icon={faXmark} />
        </button>

        {pagamentoConfirmado && (
          <div className="pix-modal-sucesso">
            <FontAwesomeIcon icon={faCircleCheck} className="pix-modal-sucesso-icone" />
            <h3>Pagamento confirmado!</h3>
            <p>
              <img src="/moeda.png" alt="Moeda" className="pix-modal-sucesso-moeda" />
              {moedas} moedas depositadas na sua conta.
            </p>

            {/* 🆕 destaque do bônus de primeira recarga */}
            {bonusMoedas > 0 && (
              <div className="pix-modal-bonus">
                <img src="/moeda.png" alt="Moeda" className="pix-modal-bonus-icone" />
                <span>+{bonusMoedas} de bônus de boas-vindas! 🎉</span>
              </div>
            )}

            <button className="pix-modal-btn-fechar" onClick={onClose}>
              Fechar
            </button>
          </div>
        )}

        {!pagamentoConfirmado && carregando && (
          <div className="pix-modal-carregando">
            <div className="pix-modal-spinner" />
            <p>Gerando seu Pix...</p>
          </div>
        )}

        {!pagamentoConfirmado && !carregando && erro && (
          <div className="pix-modal-erro">
            <FontAwesomeIcon icon={faCircleExclamation} className="pix-modal-erro-icone" />
            <p>{erro}</p>
            <button className="pix-modal-btn-fechar" onClick={onClose}>
              Fechar
            </button>
          </div>
        )}

        {!pagamentoConfirmado && !carregando && !erro && pixCode && (
          <div className="pix-modal-conteudo">
            <h3 className="pix-modal-titulo">
              <img src="/moeda.png" alt="Moeda" className="pix-modal-titulo-moeda" />
              {moedas} moedas
            </h3>

            {qrcodeImage && (
              <div className="pix-modal-qrcode-wrapper">
                <img src={qrcodeImage} alt="QR Code Pix" className="pix-modal-qrcode" />
              </div>
            )}

            <p className="pix-modal-instrucao">
              Escaneie o QR code ou copie o código abaixo no app do seu banco
            </p>

            <div className="pix-modal-codigo-box">
              <span className="pix-modal-codigo-texto">{pixCode}</span>
            </div>

            <button className="pix-modal-btn-copiar" onClick={copiarCodigo}>
              <FontAwesomeIcon icon={copiado ? faCheck : faCopy} />
              {copiado ? "Código copiado!" : "Copiar código Pix"}
            </button>

            <div className="pix-modal-aguardando">
              <div className="pix-modal-aguardando-spinner" />
              <span>Aguardando confirmação do pagamento...</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ModalPixCheckout;